import { useState } from "react";
import { C, PageHeader, GlassCard, Button, Input, Skeleton, Badge } from "@coremonit/design-system";
import { Bot, Send, Sparkles, Activity, ListTree, Terminal, GitFork, Rocket, RotateCcw, Search } from "lucide-react";
import { useAskAI } from "../api/hooks";

type ChatItem = { role: "user"; text: string } | { role: "assistant"; text: string; answer?: import("@coremonit/types").AIAnswer };

const STARTERS = [
  "Why is payment-service slow?",
  "What changed in order-service in the last hour?",
  "Is inventory-service saturating its connection pool?",
  "Show me the top 3 failing dependencies right now.",
];

export function AIAssistantPage() {
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [text, setText] = useState("");
  const ask = useAskAI();

  const submit = async (q?: string) => {
    const question = (q ?? text).trim();
    if (!question) return;
    setText("");
    setChat((c) => [...c, { role: "user", text: question }]);
    try {
      const a = await ask.mutateAsync({ text: question });
      setChat((c) => [...c, { role: "assistant", text: a.summary, answer: a }]);
    } catch (e) {
      setChat((c) => [...c, { role: "assistant", text: `Error: ${(e as Error).message}` }]);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "calc(100vh - 100px)" }}>
      <PageHeader icon={<Bot size={16} />} title="AI Assistant" subtitle="Ask CoreMonit to investigate. Answers link straight to metrics, traces, logs, and deploys." badge={<Badge color="purple"><Sparkles size={9} /> AI</Badge>} />
      <GlassCard style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {chat.length === 0 && (
            <div style={{ marginTop: "auto", marginBottom: "auto", textAlign: "center" }}>
              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: 18, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, marginBottom: 14, boxShadow: `0 0 30px rgba(45,127,249,0.4)` }}>
                <Sparkles size={22} color="#fff" />
              </div>
              <div style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Ask anything about your fleet.</div>
              <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 16 }}>Try one of these to get started:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", maxWidth: 640, margin: "0 auto" }}>
                {STARTERS.map((s) => (
                  <button key={s} onClick={() => submit(s)}
                    style={{ padding: "8px 14px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 20, color: C.textSub, fontSize: 11, cursor: "pointer" }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {chat.map((m, i) => m.role === "user" ? (
            <div key={i} style={{ alignSelf: "flex-end", maxWidth: 640 }}>
              <div style={{ background: C.blue, color: "#fff", padding: "10px 14px", borderRadius: "16px 16px 4px 16px", fontSize: 12.5 }}>{m.text}</div>
            </div>
          ) : (
            <AssistantMessage key={i} text={m.text} answer={m.answer} />
          ))}
          {ask.isPending && <AssistantMessage typing />}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Input value={text} onChange={setText}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(); }}
              placeholder="Ask CoreMonit…" size="md" prefix={<Search size={13} />} />
          </div>
          <Button variant="glow" size="md" icon={<Send size={12} />} onClick={() => submit()} disabled={!text.trim()} loading={ask.isPending}>Send</Button>
        </div>
      </GlassCard>
    </div>
  );
}

function AssistantMessage({ text, answer, typing }: { text?: string; answer?: import("@coremonit/types").AIAnswer; typing?: boolean }) {
  return (
    <div style={{ alignSelf: "flex-start", maxWidth: 720, display: "flex", gap: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Sparkles size={13} color="#fff" />
      </div>
      <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, padding: "12px 14px", borderRadius: "16px 16px 16px 4px", fontSize: 12.5, color: C.textSub, flex: 1 }}>
        {typing ? (
          <div style={{ display: "flex", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue, animation: "cm-ai-dot 1.2s ease-in-out infinite" }} />
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue, animation: "cm-ai-dot 1.2s ease-in-out infinite", animationDelay: "0.15s" }} />
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue, animation: "cm-ai-dot 1.2s ease-in-out infinite", animationDelay: "0.3s" }} />
          </div>
        ) : (
          <>
            <div style={{ color: C.text, lineHeight: 1.6, marginBottom: answer ? 10 : 0 }}>{text}</div>
            {answer && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                <EvidenceGroup icon={<Activity size={10} />}  label="Metrics"      items={answer.metrics} />
                <EvidenceGroup icon={<ListTree size={10} />}  label="Traces"       items={answer.traces} />
                <EvidenceGroup icon={<Terminal size={10} />}  label="Logs"         items={answer.logs} />
                <EvidenceGroup icon={<GitFork size={10} />}   label="Dependencies" items={answer.dependencies} />
                <EvidenceGroup icon={<Rocket size={10} />}    label="Deployments"  items={answer.deployments} />
                {answer.possibleRollback && <EvidenceGroup icon={<RotateCcw size={10} />} label="Rollback" items={[answer.possibleRollback.label]} />}
              </div>
            )}
            {answer && (
              <div style={{ marginTop: 12, padding: 10, background: C.blueDim, border: `1px solid ${C.borderBright}`, borderRadius: 10 }}>
                <div style={{ color: C.blue, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Suggested fixes</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: C.text, fontSize: 11.5, lineHeight: 1.6 }}>
                  {answer.suggestedFixes.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EvidenceGroup({ icon, label, items }: { icon: React.ReactNode; label: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, marginBottom: 4 }}>{icon}{label}</div>
      {items.slice(0, 3).map((i) => (
        <div key={i} style={{ color: C.textSub, fontSize: 10, fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{i}</div>
      ))}
    </div>
  );
}
